/**
 * BenchmarkThermometerCard.js - Card de Posição com Termômetro
 * 
 * Exibe a posição do usuário em um termômetro horizontal para a HomeScreen.
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Animated, Easing, Platform } from 'react-native';
import { Text } from '../ui/AppText';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path } from 'react-native-svg';

// Tom das curvas decorativas e da marca d'agua do livro. Fica proximo do fundo
// do card (#FCF0B6) de proposito: a diferenca e sutil, so da relevo.
const CURVE_COLOR = '#FCECA1';

const BenchmarkThermometerCard = ({
    position = 234,
    totalCandidates = 4521,
    percentile = 5.2,
    weeklyChange = 12,
    userPhoto = null,
    onPress,
    isDarkMode = true,
}) => {
    // Animação de pulso apenas
    const pulseAnim = useRef(new Animated.Value(1)).current;
    // Animação do skeleton
    const skeletonAnim = useRef(new Animated.Value(0.3)).current;
    // Estado de carregamento da foto
    const [photoLoading, setPhotoLoading] = useState(true);

    // Calcular posição do indicador (0-100%)
    const indicatorPosition = totalCandidates > 0
        ? ((totalCandidates - position) / totalCandidates) * 100
        : 0;

    // Determinar tier baseado no percentile
    const getTierColor = () => {
        if (percentile <= 10) return ['#FFD700', '#FFA500'];  // Ouro
        if (percentile <= 25) return ['#C0C0C0', '#A8A8A8'];  // Prata
        if (percentile <= 50) return ['#CD7F32', '#8B4513'];  // Bronze
        return ['#6B7280', '#4B5563'];  // Cinza
    };

    const tierColors = getTierColor();

    useEffect(() => {
        // Animação de pulso contínuo - MAIS LENTA para suavidade
        Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, {
                    toValue: 1.12,
                    duration: 1500,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
                Animated.timing(pulseAnim, {
                    toValue: 1,
                    duration: 1500,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
            ])
        ).start();

        // Animação do skeleton (pulso de opacidade)
        Animated.loop(
            Animated.sequence([
                Animated.timing(skeletonAnim, {
                    toValue: 0.7,
                    duration: 800,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
                Animated.timing(skeletonAnim, {
                    toValue: 0.3,
                    duration: 800,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
            ])
        ).start();
    }, []);

    // Amarelo claro CHAPADO (#FCF0B6) com tipografia preta. Os 2 stops iguais
    // anulam o gradiente sem trocar o LinearGradient por View. FIXO nos dois
    // temas: o card e claro, entao nao pode escurecer no tema dark.
    // Preto sobre #FCF0B6 = 18,3:1; o secundario a 70% = 8:1.
    // Borda e trilho passaram a ser PRETOS translucidos — em branco sumiriam.
    const cardColors = ['#FCF0B6', '#FCF0B6'];
    const cardBorderColor = 'rgba(0, 0, 0, 0.12)';
    const textPrimary = '#000000';
    const textSecondary = 'rgba(0, 0, 0, 0.7)';
    const thermometerBg = 'rgba(0, 0, 0, 0.14)';

    // Posição direta do indicador (sem animação de deslizamento)
    const indicatorLeftValue = `${indicatorPosition}%`;

    return (
        <TouchableOpacity
            style={styles.container}
            onPress={onPress}
            activeOpacity={0.8}
        >
            <LinearGradient
                colors={cardColors}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.gradient, { borderColor: cardBorderColor }]}
            >
                {/* Camada decorativa (atras do conteudo): duas ondas + marca
                    d'agua de livro. `pointerEvents=none` p/ nao roubar o toque.
                    O viewBox 0-100 com preserveAspectRatio="none" faz as curvas
                    acompanharem qualquer largura/altura do card. */}
                <View style={StyleSheet.absoluteFill} pointerEvents="none">
                    <Svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {/* Onda superior */}
                        <Path d="M0,0 H100 V15 C74,29 34,6 0,23 Z" fill={CURVE_COLOR} />
                        {/* Varredura do canto inferior esquerdo */}
                        <Path d="M0,72 C16,86 33,92 45,100 H0 Z" fill={CURVE_COLOR} />
                    </Svg>
                </View>

                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.titleRow}>
                        <Text style={styles.icon}>🎯</Text>
                        <Text style={[styles.title, { color: textPrimary }]}>Minha Posição</Text>
                        {/* Livro clareado, logo apos o titulo. Inline no titleRow
                            (nao absoluto) p/ acompanhar o texto em qualquer
                            largura ou escala de fonte do sistema. */}
                        <Svg width={22} height={22} viewBox="0 0 24 24">
                            <Path
                                d="M3,5 C6,3.4 9,3.4 11.4,5 V19 C9,17.4 6,17.4 3,19 Z"
                                fill={CURVE_COLOR}
                            />
                            <Path
                                d="M12.6,5 C15,3.4 18,3.4 21,5 V19 C18,17.4 15,17.4 12.6,19 Z"
                                fill={CURVE_COLOR}
                            />
                        </Svg>
                    </View>
                    <View style={[styles.changeBadge, { backgroundColor: weeklyChange >= 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)' }]}>
                        <Text style={[styles.changeText, { color: weeklyChange >= 0 ? '#10B981' : '#EF4444' }]}>
                            {weeklyChange >= 0 ? '▲' : '▼'} {Math.abs(weeklyChange)}
                        </Text>
                    </View>
                </View>

                {/* Termômetro Horizontal */}
                <View style={styles.thermometerContainer}>
                    {/* Background Track */}
                    <View style={[styles.thermometerTrack, { backgroundColor: thermometerBg }]}>
                        {/* Gradient Fill */}
                        <LinearGradient
                            colors={['#EF4444', '#F59E0B', '#10B981']}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.thermometerFill}
                        />

                        {/* Marcadores de divisão */}
                        <View style={[styles.marker, { left: '25%' }]} />
                        <View style={[styles.marker, { left: '50%' }]} />
                        <View style={[styles.marker, { left: '75%' }]} />
                    </View>

                    {/* Indicador do Usuário */}
                    <View
                        style={[
                            styles.indicatorContainer,
                            { left: indicatorLeftValue },
                        ]}
                    >
                        <Animated.View
                            style={[
                                styles.indicator,
                                { transform: [{ scale: pulseAnim }] },
                            ]}
                        >
                            <LinearGradient
                                colors={tierColors}
                                style={styles.indicatorGradient}
                            >
                                {userPhoto ? (
                                    <>
                                        {photoLoading && (
                                            <Animated.View
                                                style={[
                                                    styles.userPhoto,
                                                    styles.skeleton,
                                                    { opacity: skeletonAnim }
                                                ]}
                                            />
                                        )}
                                        <Image
                                            source={{ uri: userPhoto }}
                                            style={[styles.userPhoto, photoLoading && { position: 'absolute', opacity: 0 }]}
                                            contentFit="cover"
                                            onLoad={() => setPhotoLoading(false)}
                                        />
                                    </>
                                ) : (
                                    <Text style={styles.indicatorIcon}>👤</Text>
                                )}
                            </LinearGradient>
                        </Animated.View>
                        {/* Linha vertical */}
                        <View style={[styles.indicatorLine, { backgroundColor: tierColors[0] }]} />
                    </View>

                    {/* Labels */}
                    <View style={styles.labelsRow}>
                        <Text style={[styles.labelText, { color: textSecondary }]}>Inicio</Text>
                        <Text style={[styles.labelText, { color: textSecondary }]}>Top 50%</Text>
                        <Text style={[styles.labelText, { color: textSecondary }]}>Top 10%</Text>
                    </View>
                </View>
            </LinearGradient>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    container: {
        borderRadius: 14,
        marginBottom: 12,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 4,
    },
    gradient: {
        paddingHorizontal: 12,
        paddingTop: 10,
        paddingBottom: 8,
        borderRadius: 14,
        borderWidth: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    icon: {
        fontSize: 18,
    },
    title: {
        fontSize: 15,
        fontWeight: '700',
    },
    changeBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
    },
    changeText: {
        fontSize: 12,
        fontWeight: '700',
    },
    thermometerContainer: {
        marginBottom: 0,
        paddingTop: 20,
    },
    thermometerTrack: {
        height: 10,
        borderRadius: 5,
        overflow: 'hidden',
        position: 'relative',
    },
    thermometerFill: {
        ...StyleSheet.absoluteFillObject,
        borderRadius: 6,
    },
    marker: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
    indicatorContainer: {
        position: 'absolute',
        top: 0,
        alignItems: 'center',
        marginLeft: -16,
    },
    indicator: {
        width: 32,
        height: 32,
        borderRadius: 16,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 4,
    },
    indicatorGradient: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    indicatorIcon: {
        fontSize: 16,
    },
    userPhoto: {
        width: 28,
        height: 28,
        borderRadius: 14,
    },
    skeleton: {
        backgroundColor: '#4B5563',
        position: 'absolute',
    },
    indicatorLine: {
        width: 2,
        height: 14,
        borderRadius: 1,
        marginTop: -2,
    },
    labelsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 6,
        paddingHorizontal: 4,
    },
    labelText: {
        fontSize: 10,
    },
});

export default BenchmarkThermometerCard;
